import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiKeyService, ApiKeyView } from '../../api/services';
import { TrashIcon } from '@heroicons/react/24/outline';

const ApiKeys: React.FC = () => {
  const [keys, setKeys] = useState<ApiKeyView[]>([]);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState('');
  const [justCreated, setJustCreated] = useState<ApiKeyView | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => apiKeyService.list().then(setKeys).catch((e) => toast.error(e?.response?.data?.message || 'Erreur'));

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setBusy(true);
    try {
      const k = await apiKeyService.create(name, scopes || undefined);
      setJustCreated(k);
      setName(''); setScopes('');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur de création');
    } finally { setBusy(false); }
  };

  const revoke = async (id: number) => {
    if (!window.confirm('Révoquer définitivement cette clé ?')) return;
    try { await apiKeyService.revoke(id); load(); toast.success('Clé révoquée'); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Erreur'); }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Clés d'API</h2>
      <p className="text-sm text-gray-500">
        Les clés permettent un accès programmatique à votre tenant. Rate-limit : 100 req/min par clé.
        Disponible sur le plan <strong>Business</strong>.
      </p>

      {justCreated && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg text-sm space-y-2">
          <p className="font-semibold text-amber-900">Clé créée — copiez-la maintenant, elle ne sera plus affichée.</p>
          <code className="block bg-white border border-amber-200 rounded px-3 py-2 font-mono text-xs break-all">
            {justCreated.plainKey}
          </code>
          <button onClick={() => setJustCreated(null)} className="text-xs text-amber-700 underline">J'ai copié, masquer</button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-end bg-white p-4 rounded-xl border border-gray-200">
        <div className="flex-1 min-w-40">
          <label className="block text-xs text-gray-600 mb-1">Nom</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: import-compta"
                 className="w-full rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <div className="flex-1 min-w-48">
          <label className="block text-xs text-gray-600 mb-1">Scopes (optionnel, virgules)</label>
          <input value={scopes} onChange={(e) => setScopes(e.target.value)} placeholder="documents:read,clients:read"
                 className="w-full rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <button onClick={create} disabled={busy}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
          Créer
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Nom</th>
              <th className="px-4 py-3 text-left">Préfixe</th>
              <th className="px-4 py-3 text-left">Scopes</th>
              <th className="px-4 py-3 text-left">Créée le</th>
              <th className="px-4 py-3 text-left">Dernière utilisation</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {keys.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">Aucune clé</td></tr>}
            {keys.map((k) => (
              <tr key={k.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{k.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{k.keyPrefix}…</td>
                <td className="px-4 py-3 text-xs text-gray-500">{k.scopes || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{new Date(k.createdAt).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${k.revoked ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-800'}`}>
                    {k.revoked ? 'Révoquée' : 'Active'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {!k.revoked && (
                    <button onClick={() => revoke(k.id)} className="text-red-600 hover:text-red-800">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ApiKeys;
